/** Loads Adobe IMS (`imslib.min.js`) and sets `window.adobeid` when missing. */

/* eslint-disable @typescript-eslint/no-explicit-any */

const IMS_LIB_STAGE =
  "https://auth-stg1.services.adobe.com/imslib/imslib.min.js";
const IMS_LIB_PROD = "https://auth.services.adobe.com/imslib/imslib.min.js";
const SCRIPT_ATTR = "data-dxp-ims-bootstrap";

function getPartnerCfg(): any {
  return typeof window !== "undefined"
    ? (window as any).partnerRegistrationConfig
    : null;
}

/** Full redirect URI for IMS (same idea as getDXPRegistrationLink). */
function getDxpImsRedirectUri(): string {
  if (typeof window === "undefined") return "";
  const pathSplit = window.location.pathname?.split("registration");
  const prefix = pathSplit?.[0] ?? "";
  return `${window.location.origin}${prefix}registration`;
}

function imsLibScriptPresent(): boolean {
  return !!document.querySelector(
    `script[src*="imslib"], script[${SCRIPT_ATTR}]`,
  );
}

/**
 * Ensures imslib is loaded and adobeIMS is initialized. Safe to call multiple times.
 */
export function ensureDxpAdobeImsBootstrapped(): Promise<void> {
  if (typeof window === "undefined") {
    return Promise.resolve();
  }

  const w = window as any;
  if (w.adobeIMS?.initialized) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const cfg = getPartnerCfg();
    if (!cfg?.REACT_APP_CLEINT_ID) {
      reject(
        new Error(
          "partnerRegistrationConfig.REACT_APP_CLEINT_ID is missing; cannot load IMS",
        ),
      );
      return;
    }

    const dfEnv = cfg.REACT_APP_DYNAMIC_FORM_ENVIRONMENT || "stage";
    const useProdLib = dfEnv === "prod";
    const imsEnvironment = useProdLib ? "prod" : "stg1";

    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      resolve();
    };

    window.addEventListener("adobeIMS:profileReady", finish, { once: true });

    const poll = () => {
      if (w.adobeIMS?.initialized) {
        finish();
        return;
      }
      window.setTimeout(poll, 50);
    };
    poll();
    window.setTimeout(finish, 25000);

    if (!w.adobeid) {
      w.adobeid = {
        client_id: cfg.REACT_APP_CLEINT_ID,
        environment: imsEnvironment,
        scope: "AdobeID,openid",
        locale: "en_US",
        redirect_uri: getDxpImsRedirectUri(),
        async onReady() {
          window.dispatchEvent(new Event("adobeIMS:profileReady"));
        },
      };
    }

    if (imsLibScriptPresent()) {
      return;
    }

    const imsLib = document.createElement("script");
    imsLib.src = useProdLib ? IMS_LIB_PROD : IMS_LIB_STAGE;
    imsLib.async = true;
    imsLib.setAttribute(SCRIPT_ATTR, "true");
    imsLib.onerror = () => {
      if (!settled) {
        settled = true;
        reject(new Error("Failed to load Adobe imslib.min.js"));
      }
    };
    document.body.appendChild(imsLib);
  });
}
